import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  CheckCircle2,
  Droplets,
  Search,
  Hammer,
  ShieldCheck,
  AlertTriangle,
} from "lucide-react";
import { SEO } from "@/components/SEO";
import { ServiceAreasSection } from "@/components/CrossLinks";
import { eavestroughSchema } from "./schemas/eavestroughSchema";

export default function Eavestrough() {
  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title="Eavestrough Installation Saskatchewan | Seamless Gutters"
        description="Professional eavestrough and gutter installation in Saskatchewan. We install seamless gutters to protect your foundation from water damage."
        schema={eavestroughSchema}
      />

      {/* Hero */}
      <section className="relative py-32 text-primary-foreground overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/a45bf747-d0d6-427b-adfb-d9ba7d5beb9b.png&output=webp&q=60"
            alt="Seamless Eavestrough Installation"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
        </div>
        <div className="container relative px-4 z-10">
          <div className="max-w-3xl space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white drop-shadow-md">
              Eavestrough & Gutter Installation Saskatchewan
            </h1>
            <p className="text-xl text-white/90 drop-shadow-sm font-medium">
              Protect your home's foundation and siding. We install custom,
              seamless eavestroughs designed to handle heavy prairie downpours
              and spring snowmelt.
            </p>
            <div className="pt-4 flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg"
                asChild
              >
                <Link to="/contact">Get a Free Gutter Estimate</Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-transparent text-white border-white hover:bg-white/10 shadow-lg"
                asChild
              >
                <a href="tel:6397397969">Call Now (639) 739-7969</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* When you need it & Warning Signs */}
      <section className="py-20 bg-background">
        <div className="container px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold">
                When You Need New Eavestroughs
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Your gutters are the unsung heroes of your home's exterior. When
                they fail, water pours directly down your siding and pools at
                your foundation, leading to flooded basements and structural
                shifting—a major issue in Saskatchewan's clay soils.
              </p>

              <div className="space-y-4 my-8">
                <h3 className="text-xl font-bold text-foreground flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-accent" />
                  Signs Your Gutters Are Failing
                </h3>
                <ul className="space-y-4">
                  <li className="flex gap-4">
                    <span className="text-3xl font-bold text-primary/20 leading-none">
                      01
                    </span>
                    <div>
                      <strong className="text-foreground block">
                        Leaking Seams
                      </strong>
                      <span className="text-muted-foreground">
                        Water dripping from the joints of older sectional
                        gutters.
                      </span>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <span className="text-3xl font-bold text-primary/20 leading-none">
                      02
                    </span>
                    <div>
                      <strong className="text-foreground block">
                        Sagging or Pulling Away
                      </strong>
                      <span className="text-muted-foreground">
                        Gutters leaning away from the fascia board, often caused
                        by heavy ice buildup.
                      </span>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <span className="text-3xl font-bold text-primary/20 leading-none">
                      03
                    </span>
                    <div>
                      <strong className="text-foreground block">
                        Water in the Basement
                      </strong>
                      <span className="text-muted-foreground">
                        If water is pooling near your foundation, your
                        downspouts aren't doing their job.
                      </span>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            <div className="space-y-8">
              <div className="bg-muted/30 p-8 rounded-xl border">
                <h3 className="text-2xl font-bold mb-6">
                  Why It Suits Saskatchewan
                </h3>
                <ul className="space-y-4">
                  {[
                    "Seamless design prevents ice from expanding and breaking joints in winter.",
                    "Heavy-gauge aluminum withstands snow load and resists rust.",
                    'Properly sized 5" or 6" profiles handle sudden, torrential summer thunderstorms.',
                    "Protects your foundation from shifting—a critical issue in Saskatchewan clay soils.",
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3 text-muted-foreground">
                      <CheckCircle2 className="w-6 h-6 text-primary shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-xl h-64">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/a45bf747-d0d6-427b-adfb-d9ba7d5beb9b.png&output=webp&q=60"
                  alt="Seamless Gutter Corner Close-up"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Materials We Use */}
      <section className="py-20 bg-muted/30 border-y">
        <div className="container px-4">
          <div className="max-w-3xl mx-auto space-y-6 text-center">
            <h2 className="text-3xl font-bold">
              Materials We Use for Eavestroughs
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              We exclusively install seamless aluminum eavestroughs,
              custom-extruded on-site to perfectly match the dimensions of your
              home. We use heavy-gauge aluminum (.023 or .027 thickness) that
              won't warp or rust, secured with premium hidden hangers rather
              than old-fashioned spikes that pull out over time. We offer both
              5-inch standard and 6-inch high-capacity profiles to handle
              massive prairie downpours. To keep your system flowing freely, we
              also install premium aluminum leaf guards that block debris while
              allowing water to enter.
            </p>
          </div>
        </div>
      </section>

      {/* Cost & Comparison Table */}
      <section className="py-20 bg-background">
        <div className="container px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold">What It Costs</h2>

              <div className="bg-primary/5 border border-primary/20 p-6 rounded-xl">
                <h3 className="font-bold text-xl mb-2 text-foreground">
                  Estimated Cost: $8 – $15 per linear foot
                </h3>
                <p className="text-sm text-muted-foreground italic">
                  *An average home usually costs between $1,200 and $2,500 for a
                  complete seamless system.
                </p>
              </div>

              <p className="text-lg text-muted-foreground leading-relaxed">
                The cost for seamless eavestrough installation depends on the
                linear footage of your roofline, the profile size (5-inch vs
                6-inch), and the number of corners and downspouts required.
                Adding leaf guards will increase the initial cost but save you
                money and danger on annual cleanings. We provide honest,
                fixed-price quotes. See our{" "}
                <Link
                  to="/roofing-cost-guide-saskatchewan"
                  className="text-accent hover:underline"
                >
                  Saskatchewan Roofing Cost Guide
                </Link>{" "}
                for more pricing context.
              </p>
            </div>

            <div className="relative rounded-2xl overflow-hidden shadow-xl h-80">
              <img
                src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/a45bf747-d0d6-427b-adfb-d9ba7d5beb9b.png&output=webp&q=60"
                alt="Premium Leaf Guard Installation"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border bg-background shadow-sm max-w-4xl mx-auto">
            <h3 className="text-xl font-bold p-4 bg-muted/30 border-b">
              Seamless vs. Sectional Gutters
            </h3>
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-primary text-primary-foreground">
                  <th className="p-4 font-bold border-b border-primary-foreground/20">
                    Feature
                  </th>
                  <th className="p-4 font-bold border-b border-primary-foreground/20">
                    Seamless (Our Standard)
                  </th>
                  <th className="p-4 font-bold border-b border-primary-foreground/20">
                    Sectional (DIY Store)
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                <tr className="hover:bg-muted/50 transition-colors">
                  <td className="p-4 font-medium text-foreground">
                    Leak Potential
                  </td>
                  <td className="p-4 text-muted-foreground font-bold text-primary">
                    Very Low (Only at corners)
                  </td>
                  <td className="p-4 text-muted-foreground">
                    High (Leaks at every joint)
                  </td>
                </tr>
                <tr className="hover:bg-muted/50 transition-colors">
                  <td className="p-4 font-medium text-foreground">
                    Aesthetic Appeal
                  </td>
                  <td className="p-4 text-muted-foreground font-bold text-primary">
                    Clean, smooth lines
                  </td>
                  <td className="p-4 text-muted-foreground">
                    Visible seams every 10 feet
                  </td>
                </tr>
                <tr className="hover:bg-muted/50 transition-colors">
                  <td className="p-4 font-medium text-foreground">
                    Winter Durability
                  </td>
                  <td className="p-4 text-muted-foreground font-bold text-primary">
                    Excellent (No joints to split)
                  </td>
                  <td className="p-4 text-muted-foreground">
                    Poor (Ice expands seams)
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-20 bg-muted/30 border-y">
        <div className="container px-4">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Our Installation Process
            </h2>
            <p className="text-lg text-muted-foreground">
              Custom-fabricated on your driveway for a perfect fit.
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8 relative">
            <div className="hidden md:block absolute top-8 left-[10%] right-[10%] h-0.5 bg-border -z-10"></div>

            <div className="relative space-y-4 bg-muted/30 pt-2 text-center md:text-left">
              <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center border-4 border-muted/30 mx-auto md:mx-0 shadow-sm">
                <Search className="w-6 h-6 text-accent" />
              </div>
              <h3 className="text-xl font-bold">1. Measurement & Removal</h3>
              <p className="text-muted-foreground">
                Precise calculation of the required footage, followed by the
                safe removal of your old gutters.
              </p>
            </div>

            <div className="relative space-y-4 bg-muted/30 pt-2 text-center md:text-left">
              <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center border-4 border-muted/30 mx-auto md:mx-0 shadow-sm">
                <Hammer className="w-6 h-6 text-accent" />
              </div>
              <h3 className="text-xl font-bold">2. On-Site Fabrication</h3>
              <p className="text-muted-foreground">
                We extrude custom-length seamless aluminum directly from our
                specialized truck machine.
              </p>
            </div>

            <div className="relative space-y-4 bg-muted/30 pt-2 text-center md:text-left">
              <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center border-4 border-muted/30 mx-auto md:mx-0 shadow-sm">
                <ShieldCheck className="w-6 h-6 text-accent" />
              </div>
              <h3 className="text-xl font-bold">3. Secure Installation</h3>
              <p className="text-muted-foreground">
                Securing the system with hidden hangers, ensuring the perfect
                slope for efficient drainage.
              </p>
            </div>

            <div className="relative space-y-4 bg-muted/30 pt-2 text-center md:text-left">
              <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center border-4 border-muted/30 mx-auto md:mx-0 shadow-sm">
                <Droplets className="w-6 h-6 text-accent" />
              </div>
              <h3 className="text-xl font-bold">4. Water Testing</h3>
              <p className="text-muted-foreground">
                We test the flow to ensure water moves rapidly to the downspouts
                and away from your foundation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <ServiceAreasSection
        service="Eavestrough Installation"
        serviceSlug="eavestrough"
      />

      {/* FAQ */}
      <section className="py-20 bg-background border-y">
        <div className="container px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-center mb-12">
            Eavestrough FAQ
          </h2>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger className="text-lg font-medium text-left">
                Why are seamless eavestroughs better?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base">
                Seamless eavestroughs are custom-cut on site to the exact length
                of your roofline. Because they have no seams (except at
                corners), they are far less likely to leak or sag over time.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger className="text-lg font-medium text-left">
                Do you install leaf guards?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base">
                Yes, we can install premium gutter guards that keep leaves and
                debris out while allowing water to flow freely, reducing your
                maintenance needs.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger className="text-lg font-medium text-left">
                Can you install gutters at the same time as a new roof?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base">
                Absolutely. It's often the best time to upgrade your
                eavestroughs. We can coordinate the{" "}
                <Link
                  to="/services/replacement"
                  className="underline text-primary"
                >
                  roof replacement
                </Link>{" "}
                and gutter installation for a seamless exterior upgrade.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Cross Links & CTA Banner */}
      <section className="py-20 bg-primary text-primary-foreground text-center">
        <div className="container px-4 max-w-4xl space-y-8">
          <h2 className="text-3xl md:text-4xl font-bold">
            Protect Your Home's Foundation
          </h2>
          <p className="text-xl text-primary-foreground/90 leading-relaxed">
            We serve homeowners across Saskatoon, Regina, Moose Jaw, Prince
            Albert, Swift Current, Yorkton, Estevan, and Weyburn. Most gutter
            installations take just one day.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 pt-4">
            <Button
              size="lg"
              className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8 py-6 h-auto"
              asChild
            >
              <Link to="/contact">Get Your Free Gutter Estimate</Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="text-lg px-8 py-6 h-auto bg-transparent border-white text-white hover:bg-white/10"
              asChild
            >
              <Link to="/services/replacement">Explore Roof Replacements</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
